import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaselinkService, LinkConfigEntry } from '../../../../core/base-api';
import { ActivatedRoute } from '@angular/router';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AppCustomConfigService } from '../../../../core/services/app-custom-config.service';
import { Subscription } from 'rxjs';
import { markFormAsTouched } from '../../../../shared/functions/form.functions';
import { ViewMode } from '../../../../ech0134/meldungsempfaenger/view-mode.enum';
import { deepCopy } from '../../../../shared/functions/deep-copy.functions';
import { FormsModule, NgForm } from '@angular/forms';
import { DirtyCheckModalComponent } from '../../../../shared/components/modals/dirty-check-modal/dirty-check-modal.component';
import { Location } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CapAlertService } from '../../../../core/extension/exceptions/cap-alert.service';
import { DeleteCheckModalComponent } from '../../../../shared/components/modals/delete-check-modal/delete-check-modal.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapFormColumnsDirective } from '../../../../shared/form-control/form-columns.directive';
import { ContentHeaderComponent } from '../../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../../shared/components/title-functionbar/title-functionbar.component';
import { CapFormRowDirective } from '../../../../shared/form-control/cap-form-row.directive';
import { CapFormControlComponent } from '../../../../shared/form-control/form-control.component';
import { AutofocusDirective } from '../../../../shared/directives/autofocus.directive';
import { MetaDisablerDirective } from '../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../shared/directives/meta-validator.directive';

@Component({
  selector: 'cap-link-edit',
  templateUrl: './link-edit.component.html',
  styleUrls: ['./link-edit.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    CapFormRowDirective,
    CapFormControlComponent,
    AutofocusDirective,
    MetaDisablerDirective,
    MetaValidatorDirective,
    TranslatePipe
  ]
})
export class LinkEditComponent implements OnInit, OnDestroy {
  private viewMode: ViewMode;

  @Input()
  titleKey: string;

  @ViewChild('form', { static: false })
  private form: NgForm;

  private subscription: Subscription = new Subscription();

  writeModel: LinkConfigEntry;

  readModel: LinkConfigEntry;

  id: any;

  static format(entry: LinkConfigEntry) {
    let name, id;
    if (entry.title) {
      name = entry.title;
    }
    if (entry.id) {
      id = '(ID: ' + entry.id + ')';
    }
    return [name, id].filter(Boolean).join(' - ');
  }

  constructor(
    private location: Location,
    private modalService: NgbModal,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private linkService: AppCustomConfigService,
    private baselinkService: BaselinkService,
    private capAlertService: CapAlertService
  ) {
    keysToTranslate<string>([
      'configuration.deutscherName',
      'configuration.deutscherLink',
      'configuration.franzoesischerLink',
      'configuration.franzoesischerName',
      'configuration.titel',
      'configuration.position',
      'wurdeErfolgreichGespeichert',
      'wurdeErfolgreichGeloescht',
      'Speichern',
      'Löschen'
    ]);
  }

  ngOnInit(): void {
    this.viewMode = this.route.snapshot.data.viewMode;
    this.titleKey = this.route.snapshot.queryParamMap.get('titleKey');
    const rootMenuElement = this.route.snapshot.queryParamMap.get('rootMenuElement');
    if (this.viewMode === ViewMode.NEW) {
      this.initFromTemplate(rootMenuElement);
    } else {
      this.initFromExistingData();
    }
  }

  private initFromExistingData() {
    this.route.params.subscribe((params) => {
      this.id = params['id'];
    });
    this.subscription.add(
      this.linkService.getLinkConfig(this.id).subscribe((value) => {
        this.writeModel = value;
        this.readModel = deepCopy(this.writeModel);
      })
    );
  }

  private initFromTemplate(rootMenuElement: string) {
    this.subscription.add(
      this.baselinkService.getLinkTemplate(rootMenuElement).subscribe((template) => {
        this.writeModel = template;
        this.readModel = deepCopy(this.writeModel);
      })
    );
  }

  onSubmit() {
    markFormAsTouched(this.form);
    if (this.viewMode === ViewMode.NEW) {
      this.subscription.add(
        this.linkService.createLinkConfig(this.writeModel).subscribe(() => {
          this.capAlertService.success(this.getSucccessMessage('wurdeErfolgreichGespeichert'), undefined, true);
          this.location.back();
        })
      );
    }
    if (this.viewMode === ViewMode.EDIT) {
      this.subscription.add(
        this.linkService.updateLinkConfig(this.writeModel).subscribe(() => {
          this.capAlertService.success(this.getSucccessMessage('wurdeErfolgreichGespeichert'), undefined, true);
          this.location.back();
        })
      );
    }
  }

  onDelete() {
    if (this.viewMode === ViewMode.EDIT && this.writeModel && this.writeModel.id) {
      const modal = this.modalService.open(DeleteCheckModalComponent);
      modal.componentInstance.entity = LinkEditComponent.format(this.writeModel);
      modal.result.then(
        () => {
          this.subscription.add(
            this.linkService.deleteLinkConfig(this.writeModel).subscribe(() => {
              this.capAlertService.success(this.getSucccessMessage('wurdeErfolgreichGeloescht'), undefined, true);
              this.location.back();
            })
          );
        },
        () => {}
      );
    }
  }

  private getSucccessMessage(resultKey: string) {
    return this.translate.instant('Link') + ' ' + LinkEditComponent.format(this.writeModel) + ' ' + this.translate.instant(resultKey) + '.';
  }

  onEdit() {
    this.viewMode = ViewMode.EDIT;
  }

  isEditMode(): boolean {
    return this.viewMode === ViewMode.EDIT;
  }

  isNewMode(): boolean {
    return this.viewMode === ViewMode.NEW;
  }

  isReadOnlyMode(): boolean {
    return this.viewMode === ViewMode.READ_ONLY;
  }

  onCancel() {
    if (this.viewMode === ViewMode.NEW) {
      this.location.back();
      return;
    } else if (this.viewMode === ViewMode.EDIT && this.checkDirty()) {
      this.modalService.open(DirtyCheckModalComponent).result.then(
        () => {
          this.resetModel();
        },
        () => {}
      );
      return;
    }
    this.resetModel();
  }

  checkDirty(): boolean {
    return (
      this.readModel.linkTextDe !== this.writeModel.linkTextDe ||
      this.readModel.linkDe !== this.writeModel.linkDe ||
      this.readModel.linkFr !== this.writeModel.linkFr ||
      this.readModel.linkTextFr !== this.writeModel.linkTextFr ||
      this.readModel.title !== this.writeModel.title
    );
  }

  resetModel() {
    this.writeModel = deepCopy(this.readModel);
    this.viewMode = ViewMode.READ_ONLY;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
